/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// CALCULATION UTILITIES - Centralized Business Logic Calculations
// ============================================================================
// This file consolidates all calculation utilities to eliminate duplication
// and provide a single source of truth for business calculations.
// ============================================================================

/**
 * Calculate tax amount.
 *
 * Accepts `taxRate` as decimal (0.08) or whole percent (8.0) — auto-normalized.
 * Delegates to the canonical tax engine so every surface agrees.
 *
 * @param amount - Base amount in the same unit as the returned value (cents or dollars)
 * @param taxRate - Tax rate, decimal or whole percent
 * @returns Tax amount in the same unit as `amount`
 */
export function calculateTax(amount: number, taxRate: number): number {
  const decimal = normalizeTaxRateToDecimal(taxRate);
  return amount * decimal;
}

/**
 * Calculate total with tax. Rate accepts decimal or whole percent.
 */
export function calculateTotalWithTax(amount: number, taxRate: number): number {
  return amount + calculateTax(amount, taxRate);
}

/**
 * Normalize a tax rate to decimal form (0-1).
 *
 * Estimates store tax rates as percentages (8.5 for 8.5%).
 * Invoices store tax rates as decimals (0.085 for 8.5%).
 * This utility safely converts either format to decimal.
 *
 * @param rate - Tax rate in either percentage (>1) or decimal (<=1) form
 * @returns Tax rate as a decimal (e.g. 0.085)
 */
export function normalizeTaxRateToDecimal(rate: number): number {
  if (rate <= 0) return 0;
  // Rates > 1 are percentages (e.g. 8.5); convert to decimal
  return rate > 1 ? rate / 100 : rate;
}

/**
 * Normalize a tax rate to percentage form (0-100).
 *
 * @param rate - Tax rate in either percentage (>1) or decimal (<=1) form
 * @returns Tax rate as a percentage (e.g. 8.5)
 */
export function normalizeTaxRateToPercent(rate: number): number {
  if (rate <= 0) return 0;
  // Rates <= 1 are decimals (e.g. 0.085); convert to percentage
  return rate <= 1 ? rate * 100 : rate;
}

/**
 * Calculate area in square feet
 * @param length - Length in feet
 * @param width - Width in feet
 * @returns Area in square feet
 */
export function calculateArea(length: number, width: number): number {
  return length * width;
}

/**
 * Calculate volume in cubic feet
 * @param length - Length in feet
 * @param width - Width in feet
 * @param height - Height in feet
 * @returns Volume in cubic feet
 */
export function calculateVolume(
  length: number,
  width: number,
  height: number,
): number {
  return length * width * height;
}

/**
 * Convert measurements between units
 * @param value - Value to convert
 * @param fromUnit - Source unit
 * @param toUnit - Target unit
 * @returns Converted value
 */
export function convertMeasurement(
  value: number,
  fromUnit: string,
  toUnit: string,
): number {
  const conversions: Record<string, Record<string, number>> = {
    length: {
      inches: 1,
      feet: 12,
      yards: 36,
      meters: 39.3701,
      centimeters: 0.393701,
    },
    area: {
      sq_inches: 1,
      sq_feet: 144,
      sq_yards: 1296,
      sq_meters: 1550.0031,
      sq_centimeters: 0.15500031,
    },
    volume: {
      cu_inches: 1,
      cu_feet: 1728,
      cu_yards: 46656,
      cu_meters: 61023.7441,
      cu_centimeters: 0.0610237441,
    },
  };

  // Determine conversion type
  let conversionType = "length";
  if (fromUnit.includes("sq_") || toUnit.includes("sq_"))
    conversionType = "area";
  if (fromUnit.includes("cu_") || toUnit.includes("cu_"))
    conversionType = "volume";

  const conversionTable = conversions[conversionType];
  if (!conversionTable) return value;

  const fromFactor = conversionTable[fromUnit] ?? 1;
  const toFactor = conversionTable[toUnit] ?? 1;

  return (value * fromFactor) / toFactor;
}

/**
 * Calculate percentage with proper rounding
 * @param value - Value to calculate percentage of
 * @param total - Total amount
 * @param precision - Decimal precision (default: 2)
 * @returns Percentage value
 */
export function calculatePercentage(
  value: number,
  total: number,
  precision = 2,
): number {
  if (total === 0) return 0;
  const percentage = (value / total) * 100;
  return (
    Math.round(percentage * Math.pow(10, precision)) / Math.pow(10, precision)
  );
}

/**
 * Calculate profit amount
 * @param sellingPrice - Final selling price
 * @param cost - Base cost amount
 * @returns Profit amount
 */
export function calculateProfit(sellingPrice: number, cost: number): number {
  return sellingPrice - cost;
}

/**
 * Calculate profit margin percentage
 * @param profit - Profit amount
 * @param sellingPrice - Final selling price
 * @returns Profit margin percentage (0-100)
 */
export function calculateProfitMargin(
  profit: number,
  sellingPrice: number,
): number {
  if (sellingPrice === 0) return 0;
  return (profit / sellingPrice) * 100;
}

// ============================================================================
// MARGIN-BASED UTILITIES (% of price)
// ============================================================================

/**
 * Calculate profit margin percentage (% of price)
 * margin = ((price - cost) / price) * 100
 */
export function calculateMargin(cost: number, sellingPrice: number): number {
  if (sellingPrice === 0) return 0;
  return ((sellingPrice - cost) / sellingPrice) * 100;
}

/**
 * Calculate selling price from cost and margin percentage
 * price = cost / (1 - margin/100)
 */
export function calculatePriceFromMargin(
  cost: number,
  marginPercentage: number,
): number {
  if (marginPercentage >= 100) return cost;
  return cost / (1 - marginPercentage / 100);
}

/**
 * Calculate cost from selling price and margin percentage
 * cost = price * (1 - margin/100)
 */
export function calculateCostFromMargin(
  sellingPrice: number,
  marginPercentage: number,
): number {
  return sellingPrice * (1 - marginPercentage / 100);
}
